import type { ReactNode } from 'react';
import { clsx } from './clsx';

export type CardStatus = 'ready' | 'loading' | 'error' | 'empty' | 'unconfigured';

/** Per-card accent, resolved from tailwind.config.js tokens. Never a hex. */
export type CardAccent =
  | 'btc'
  | 'sky'
  | 'music'
  | 'cal'
  | 'health'
  | 'todo'
  | 'media'
  | 'quote';

const ACCENT_BAR: Readonly<Record<CardAccent, string>> = {
  btc: 'bg-accent-btc',
  sky: 'bg-accent-sky',
  music: 'bg-accent-music',
  cal: 'bg-accent-cal',
  health: 'bg-accent-health',
  todo: 'bg-accent-todo',
  media: 'bg-accent-media',
  quote: 'bg-accent-quote',
};

export interface CardProps {
  title: string;
  /** Emoji or inline SVG, rendered at 1rem in the header. */
  icon?: ReactNode;
  accent: CardAccent;
  status?: CardStatus;
  /** Right-hand header slot: refresh button, settings gear, unit toggle. */
  action?: ReactNode;
  /** Small pill next to the title: "stale", "live", "3 alerts". */
  badge?: ReactNode;
  /** Full-bleed strip between header and body — severe weather alerts. */
  banner?: ReactNode;
  /** Pinned bottom strip — now-playing, add-item input. */
  footer?: ReactNode;
  /** false => body gets no padding (Quote's full-bleed art). Default true. */
  padded?: boolean;
  /** false => no header chrome at all (Quote renders its own title over art). */
  showHeader?: boolean;
  /** true => body scrolls internally. Default false (body clips). */
  scroll?: boolean;
  loadingLabel?: string;
  errorMessage?: string;
  emptyMessage?: string;
  unconfiguredMessage?: string;
  /** Secondary line under the state message (e.g. which env var to set). */
  hint?: ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  /** Extra control in a non-ready state, e.g. "Connect your calendar". */
  stateAction?: ReactNode;
  /** Skeleton shown while status==='loading'. Falls back to a generic one. */
  loadingSkeleton?: ReactNode;
  bodyClassName?: string;
  /** Stable hook for the verification suite, e.g. "weather-panel". */
  testId?: string;
  children: ReactNode;
}

export function Card({
  title,
  icon,
  accent,
  status = 'ready',
  action,
  badge,
  banner,
  footer,
  padded = true,
  showHeader = true,
  scroll = false,
  loadingLabel,
  errorMessage,
  emptyMessage,
  unconfiguredMessage,
  hint,
  onRetry,
  retryLabel,
  stateAction,
  loadingSkeleton,
  bodyClassName,
  testId,
  children,
}: CardProps) {
  return (
    <section
      aria-label={title}
      data-testid={testId}
      className={clsx(
        // h-full + min-h-0 + overflow-hidden is the no-scroll contract.
        'relative flex h-full min-h-0 min-w-0 flex-col overflow-hidden',
        'rounded-2xl border border-line bg-panel shadow-card transition-colors',
      )}
    >
      {/* 2px accent rail — the only per-panel colour, and it comes from a token. */}
      <div
        className={clsx('absolute inset-x-0 top-0 h-[2px] z-10', ACCENT_BAR[accent])}
        aria-hidden
      />

      {showHeader && (
        <header className="flex shrink-0 items-center gap-2 px-3 pb-2 pt-3">
          {icon !== undefined && <span className="text-base leading-none">{icon}</span>}
          <h2 className="truncate text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-muted">
            {title}
          </h2>
          {badge}
          <div className="ml-auto flex shrink-0 items-center gap-1">{action}</div>
        </header>
      )}

      {banner !== undefined && <div className="shrink-0">{banner}</div>}

      <div
        className={clsx(
          'relative min-h-0 min-w-0 flex-1',
          scroll ? 'overflow-y-auto overflow-x-hidden' : 'overflow-hidden',
          padded && 'px-3 pb-3',
          !showHeader && padded && 'pt-3',
          bodyClassName,
        )}
        data-scrollregion={scroll ? '' : undefined}
      >
        {status === 'ready' ? (
          children
        ) : status === 'loading' ? (
          (loadingSkeleton ?? <CardState variant="loading" message={loadingLabel} />)
        ) : (
          <CardState
            variant={status}
            message={
              status === 'error'
                ? errorMessage
                : status === 'empty'
                  ? emptyMessage
                  : unconfiguredMessage
            }
            hint={hint}
            onRetry={onRetry}
            retryLabel={retryLabel}
            action={stateAction}
          />
        )}
      </div>

      {footer !== undefined && (
        <div className="shrink-0 border-t border-line px-3 py-2">{footer}</div>
      )}
    </section>
  );
}

export type CardStateVariant = 'loading' | 'error' | 'empty' | 'unconfigured' | 'unavailable';

const STATE_UI: Readonly<
  Record<CardStateVariant, { glyph: string; tone: string; defaultMessage: string }>
> = {
  loading: { glyph: '◴', tone: 'text-ink-subtle', defaultMessage: 'Loading…' },
  error: { glyph: '⚠', tone: 'text-negative', defaultMessage: "Couldn't load this." },
  empty: { glyph: '○', tone: 'text-ink-subtle', defaultMessage: 'Nothing here yet.' },
  unconfigured: { glyph: '🔑', tone: 'text-warn', defaultMessage: 'Not configured.' },
  unavailable: { glyph: '—', tone: 'text-ink-subtle', defaultMessage: 'Unavailable.' },
};

/**
 * Exported standalone so it can also be used for a REGION inside an otherwise
 * healthy card — Bitcoin's fear & greed sub-section needs `unavailable`
 * without putting the whole card into a non-ready state.
 */
export function CardState({
  variant,
  message,
  hint,
  onRetry,
  retryLabel = 'Retry',
  action,
  compact = false,
  testId,
}: {
  variant: CardStateVariant;
  message?: string;
  hint?: ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  action?: ReactNode;
  compact?: boolean;
  testId?: string;
}) {
  const ui = STATE_UI[variant];
  return (
    <div
      role={variant === 'error' ? 'alert' : undefined}
      data-testid={testId}
      className={clsx(
        'flex h-full min-h-0 w-full flex-col items-center justify-center gap-2 text-center',
        compact ? 'p-2' : 'p-4',
      )}
    >
      <span
        className={clsx(
          'grid place-items-center rounded-full bg-panel-raised',
          compact ? 'h-7 w-7 text-sm' : 'h-10 w-10 text-lg',
          ui.tone,
          variant === 'loading' && 'motion-safe:animate-spin-slow',
        )}
        aria-hidden
      >
        {ui.glyph}
      </span>
      <p
        className={clsx(
          'max-w-[28ch] font-medium text-ink-muted',
          compact ? 'text-xs' : 'text-sm',
        )}
      >
        {message ?? ui.defaultMessage}
      </p>
      {hint !== undefined && (
        <p className="max-w-[34ch] text-xs leading-snug text-ink-subtle">{hint}</p>
      )}
      {action}
      {onRetry !== undefined && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-lg border border-line px-2.5 py-1 text-xs font-medium text-ink-muted transition-colors hover:bg-panel-raised focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
        >
          {retryLabel}
        </button>
      )}
    </div>
  );
}

export function SkeletonLine({ w = 'w-full', h = 'h-3' }: { w?: string; h?: string }) {
  return <div className={clsx('rounded bg-panel-raised motion-safe:animate-pulse', w, h)} />;
}

export function SkeletonBlock({ className }: { className?: string }) {
  return <div className={clsx('rounded-lg bg-panel-raised motion-safe:animate-pulse', className)} />;
}

export function SkeletonList({ rows = 4 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-2 pt-1">
      {Array.from({ length: rows }, (_, i) => (
        <SkeletonLine key={i} w={i % 2 === 0 ? 'w-full' : 'w-4/5'} />
      ))}
    </div>
  );
}

export function SkeletonChart() {
  return <SkeletonBlock className="h-full min-h-[120px] w-full" />;
}

/**
 * MANDATORY wrapper for every Recharts <ResponsiveContainer>.
 *
 * ResponsiveContainer measures its parent and sets an explicit height on
 * itself. Inside a flex column, that parent grows to fit the child, the child
 * re-measures, and the card grows past the viewport — the classic Recharts
 * infinite-growth bug and a direct failure of the no-scroll criterion. The
 * absolutely-positioned inner box is out of flow and cannot push back.
 *
 *   <ChartArea>
 *     <ResponsiveContainer width="100%" height="100%">…</ResponsiveContainer>
 *   </ChartArea>
 */
export function ChartArea({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={clsx('relative min-h-0 w-full flex-1', className)}>
      <div className="absolute inset-0">{children}</div>
    </div>
  );
}
